import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';

// Imported from the module, not the `utils` barrel: the barrel drags in the
// XState graph, which would make this helper untestable in isolation.
import { checkForErrors } from '../../../utils/check-for-errors';
import { isSelectedBotStale } from '../../../utils/selected-bot-freshness';
import { sendUserMessage } from '../../app.xstate-utils';
import type { ProStudioContext } from '../../prostudio.xstate.types';
import { reportDegradedLoad } from './report-degraded-load';

const TRANSLATION_NOT_ENABLED =
  'Channel saved, but automatic translation could not be turned on for this solution, so calls will stay in the starting language. Please contact support to enable it.';

/**
 * Switches AWS Translate on for the selected solution when a Voice channel is
 * saved with two or more languages (PSTUDIO-4519).
 *
 * Translation is gated per SOLUTION by `translateInput` / `translateOutput` in
 * bot-manager, while languages are configured per CHANNEL. Until this existed
 * the flags were set by hand through the bot-manager API, so a channel could be
 * configured for English + Spanish and never switch (AUTO-7234).
 *
 * - `languagesConfigB64` is the signal: `encodeLanguagesConfig` emits it only
 *   for 2+ languages and sends an explicit `null` otherwise.
 * - Turn ON only. Another channel on the same solution, or a chat flow using
 *   `SwitchActiveLanguage`, may rely on the flags, so dropping a channel back to
 *   one language must not switch translation off for everyone.
 * - BEST-EFFORT: it runs after the channel is saved and the caller's promise has
 *   settled, so it never rejects — a rejection would report a successful save as
 *   a failure. A failure warns the user instead, because a silent one is
 *   exactly the "configured but doesn't switch" defect this exists to remove.
 *   The warning does not say "save again": Save is disabled once the form is
 *   clean, so there would be nothing to click.
 * - The callers run it alongside the channels re-read rather than before it, so
 *   a slow bot-manager cannot hold the save flow open.
 */
export const enableSolutionTranslation = async (
  ctx: Pick<ProStudioContext, 'botName' | 'selectedBot'>,
  target: {
    botId: string;
    projectId: string;
    // `object`, not `TwilioConfigInput`: the channel events declare the payload
    // as the other codegen target's `ConfigInput`, which has no
    // `languagesConfigB64`, although the voice form always sends a
    // `TwilioConfigInput`. Read defensively.
    voiceAiConfig: object | undefined;
  }
): Promise<void> => {
  const { botId, projectId, voiceAiConfig } = target;
  const languagesConfigB64 =
    voiceAiConfig && 'languagesConfigB64' in voiceAiConfig
      ? voiceAiConfig.languagesConfigB64
      : undefined;

  if (!languagesConfigB64) {
    return;
  }

  // Only trust the flags when `selectedBot` really is this solution: it can
  // still describe the one the user just left (PSTUDIO-4121), and skipping on
  // ANOTHER solution's flags would leave this one silently off.
  const { selectedBot } = ctx;
  if (
    !isSelectedBotStale({ selectedBot, botName: ctx.botName }) &&
    selectedBot?.translateInput === true &&
    selectedBot?.translateOutput === true
  ) {
    return;
  }

  try {
    const response = await urqlGql.patchBotTranslation({
      botId,
      projectId,
      translateInput: true,
      translateOutput: true,
    });
    checkForErrors(response);
  } catch (error) {
    reportDegradedLoad(error, 'enableSolutionTranslation', {
      botId,
      projectId,
    });
    sendUserMessage({
      type: 'warning',
      text: TRANSLATION_NOT_ENABLED,
      autoClose: false,
    });
  }
};
